import { useDialog } from '@globals/dialog/useDialogStore'
import { colors } from '@globals/themes/defaultTheme'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Slide from '@mui/material/Slide'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import { CustomDialog } from '@shared/dialog/CustomDialog'
import FormErrors from '@shared/forms/FormErrors'
import Submit from '@shared/forms/Submit'
import { RecognitionFormData } from '@tenant/recognition/RecognitionModal/sendRecognitionView/useSendRecognitionForm'
import { T } from '@transifex/react'
import { useFormContext, UseFormReturn } from 'react-hook-form'
import { DEFAULT_TITLE, mobileActionStyle } from '..'
import MobileRecognitionSendView from './MobileRecognitionSendView'

export const MOBILE_SEND_RECOGNITION_DRAWER_KEY = 'mobileSendRecognitionDrawerKey'

const sendDialogHeaderStyles: SxProps = {
  '& .MuiDialog-paper': {
    borderRadius: 0,
    border: 'none',
  },
  '& .CustomDialog-title': {
    background: `${colors.blueCardGradient} !important`,
    borderBottom: 'none !important',
    '& .MuiTypography-root': {
      color: `${colors.white} !important`,
    },
  },
  '& .CustomDialog-title .MuiButton-root': {
    color: `${colors.white} !important`,
  },
  '#mobileSendRecognitionDrawerKey-close': {
    '& svg': {
      color: `${colors.white} !important`,
    },
  },
}

type MobileRecognitionSendDialogProps = {
  form: UseFormReturn<RecognitionFormData>
  handleResetRecognitionForm: () => void
  handleSaveAsDraft: () => void
  isPendingCreateOrSave: boolean
  isEditMode: boolean
  hasQualities: boolean
}

const MobileRecognitionSendDialog: React.FC<MobileRecognitionSendDialogProps> = ({
  form,
  handleResetRecognitionForm,
  handleSaveAsDraft,
  isPendingCreateOrSave,
  isEditMode,
  hasQualities,
}) => {
  const { closeDialog: closeSendRecognitionDialog } = useDialog(MOBILE_SEND_RECOGNITION_DRAWER_KEY)
  const { formState } = useFormContext<RecognitionFormData>()
  const scheduledAt = form.watch('scheduledAt')
  const isScheduledSend = !!scheduledAt

  const onSaveAsDraft = () => {
    handleSaveAsDraft()
    closeSendRecognitionDialog()
    handleResetRecognitionForm()
  }

  const handleDialogClose = () => {
    handleResetRecognitionForm()
    closeSendRecognitionDialog()
  }

  return (
    <CustomDialog
      dialogKey={MOBILE_SEND_RECOGNITION_DRAWER_KEY}
      title={DEFAULT_TITLE}
      backgroundColor={colors.grayUltraLight}
      fullScreen
      dialogStyles={sendDialogHeaderStyles}
      onClose={handleDialogClose}
      TransitionComponent={Slide}
      TransitionProps={{
        direction: 'up',
        timeout: { enter: 300, exit: 500 },
      }}>
      <Box
        sx={{
          py: 2,
          mb: 14,
        }}>
        <FormErrors errors={formState.errors} />
        <MobileRecognitionSendView hasQualities={hasQualities} />
      </Box>
      <Stack spacing={1.5} sx={mobileActionStyle}>
        <Submit
          elementId="btnSendRecognition"
          formState={form.formState}
          fullWidth
          disabled={isPendingCreateOrSave}
          submitText={isScheduledSend ? <T _str="Schedule" /> : <T _str="Send" />}
        />
        <Button
          fullWidth
          color="primary"
          id="btnSaveRecognitionAsDraft"
          variant="contained"
          disabled={isPendingCreateOrSave}
          sx={{ backgroundColor: colors.grayUltraLight, color: colors.bluePrimary }}
          onClick={onSaveAsDraft}>
          {isEditMode ? <T _str="Save" /> : <T _str="Save as draft" />}
        </Button>
      </Stack>
    </CustomDialog>
  )
}

export default MobileRecognitionSendDialog
